<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import billingPortalApi from 'dashboard/api/billingPortal';

const POLL_INTERVAL = 15000;

const isBlocked = ref(false);
const unblocked = ref(false);
const checking = ref(false);
const blockedSince = ref(null);
const accountName = ref('');
const totalCents = ref(0);
const charges = ref([]);
const copiedId = ref(null);
const blockKind = ref('overdue');

let hasLoaded = false;
let pollTimer = null;

const primaryCharge = computed(() => charges.value[0] || null);
const extraCharges = computed(() => charges.value.slice(1));
const hasCharges = computed(() => charges.value.length > 0);
// 'manual' = blocked by the finance team via the billing console (no charge to pay, no
// auto-unblock on payment). Show payment options only for an overdue block that has charges.
const isManual = computed(() => blockKind.value === 'manual');
const showPayment = computed(() => !isManual.value && hasCharges.value);

const formatBRL = cents =>
  ((cents || 0) / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

const formatDate = value =>
  value ? new Date(`${value}T00:00:00`).toLocaleDateString('pt-BR') : '';

const pixImageSrc = charge => {
  const raw = charge?.pix_qr_image_url;
  if (!raw) return null;
  if (raw.startsWith('data:') || raw.startsWith('http')) return raw;
  return `data:image/png;base64,${raw}`;
};

const payLink = charge => charge?.invoice_url || charge?.bank_slip_url || null;

const stopPolling = () => {
  if (pollTimer) {
    window.clearInterval(pollTimer);
    pollTimer = null;
  }
};

const handleUnblocked = () => {
  if (unblocked.value) return;
  unblocked.value = true;
  stopPolling();
  window.setTimeout(() => window.location.reload(), 1800);
};

const applyPayload = data => {
  if (!data) return;
  if (data.blocked === false) {
    handleUnblocked();
    return;
  }
  charges.value = data.charges || [];
  totalCents.value = data.total_cents || 0;
  accountName.value = data.account_name || '';
  blockKind.value = data.block_kind || 'overdue';
  blockedSince.value = data.blocked_since || blockedSince.value;
};

const loadPortal = async () => {
  try {
    const { data } = await billingPortalApi.show();
    applyPayload(data);
  } catch (error) {
    // Keep the static message if the portal can't be reached.
  }
};

const checkPayment = async () => {
  if (checking.value) return;
  checking.value = true;
  try {
    const { data } = await billingPortalApi.refresh();
    applyPayload(data);
  } catch (error) {
    // Ignore — the next poll will retry.
  } finally {
    checking.value = false;
  }
};

const copyPix = async charge => {
  if (!charge?.pix_payload) return;
  try {
    await navigator.clipboard.writeText(charge.pix_payload);
    copiedId.value = charge.id;
    window.setTimeout(() => {
      if (copiedId.value === charge.id) copiedId.value = null;
    }, 2500);
  } catch (error) {
    // Clipboard unavailable — the QR / payment link remain usable.
  }
};

const startPolling = () => {
  if (pollTimer) return;
  pollTimer = window.setInterval(loadPortal, POLL_INTERVAL);
};

const handleBlocked = event => {
  blockedSince.value = event?.detail?.blocked_since || blockedSince.value;
  if (isBlocked.value) return;
  isBlocked.value = true;
  if (!hasLoaded) {
    hasLoaded = true;
    loadPortal();
    startPolling();
  }
};

onMounted(() => window.addEventListener('billing:blocked', handleBlocked));
onBeforeUnmount(() => {
  window.removeEventListener('billing:blocked', handleBlocked);
  stopPolling();
});
</script>

<template>
  <div
    v-if="isBlocked"
    class="fixed inset-0 z-[10000] flex items-start justify-center overflow-y-auto bg-n-slate-1/90 p-4 backdrop-blur-sm sm:items-center"
  >
    <!-- Success / auto-unblock state -->
    <div
      v-if="unblocked"
      class="my-auto flex w-full max-w-md flex-col items-center gap-4 rounded-2xl border border-n-weak bg-n-background p-8 text-center shadow-2xl"
    >
      <div
        class="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-green-600 shadow-lg"
      >
        <span class="i-lucide-check text-3xl text-white" />
      </div>
      <h1 class="text-xl font-semibold text-n-slate-12">
        Pagamento confirmado!
      </h1>
      <p class="text-sm text-n-slate-11">
        Tudo certo. Estamos liberando o seu acesso agora mesmo…
      </p>
      <span
        class="i-lucide-loader-circle animate-spin text-2xl text-n-slate-10"
      />
    </div>

    <!-- Blocked / payment state -->
    <div
      v-else
      class="my-auto flex w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-n-weak bg-n-background shadow-2xl"
    >
      <!-- Header -->
      <div class="flex flex-col items-center gap-3 px-6 pt-8 pb-6 text-center">
        <div
          class="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-500 to-red-500 shadow-lg"
        >
          <span class="i-lucide-lock text-3xl text-white" />
        </div>
        <h1 class="text-xl font-semibold text-n-slate-12">
          {{ isManual ? 'Acesso bloqueado' : 'Acesso bloqueado por pendência' }}
        </h1>
        <p v-if="isManual" class="max-w-sm text-sm text-n-slate-11">
          O acesso{{ accountName ? ` à conta ${accountName}` : '' }} foi
          bloqueado pela nossa equipe financeira. Entre em contato para
          regularizar a situação e liberar o acesso.
        </p>
        <p v-else class="max-w-sm text-sm text-n-slate-11">
          O acesso{{ accountName ? ` à conta ${accountName}` : '' }} está
          temporariamente bloqueado por uma cobrança em atraso. Pague abaixo e o
          acesso é liberado automaticamente.
        </p>
      </div>

      <!-- Total due -->
      <div
        v-if="showPayment"
        class="mx-6 flex items-end justify-between rounded-xl border border-n-weak bg-n-solid-1 px-4 py-3"
      >
        <div class="flex flex-col">
          <span
            class="text-xs font-medium uppercase tracking-wide text-n-slate-10"
          >
            Total em aberto
          </span>
          <span class="text-2xl font-bold text-n-slate-12">
            {{ formatBRL(totalCents) }}
          </span>
        </div>
        <span class="text-xs text-n-slate-10">
          {{ charges.length }}
          {{ charges.length === 1 ? 'cobrança' : 'cobranças' }}
        </span>
      </div>

      <!-- Primary charge payment options -->
      <div v-if="showPayment && primaryCharge" class="flex flex-col gap-4 p-6">
        <div class="flex items-center justify-between">
          <div class="flex flex-col">
            <span class="text-sm font-semibold text-n-slate-12">
              {{ formatBRL(primaryCharge.amount_cents) }}
            </span>
            <span class="text-xs text-n-slate-10">
              Vencimento {{ formatDate(primaryCharge.due_date) }}
            </span>
          </div>
          <span
            v-if="primaryCharge.overdue"
            class="rounded-full bg-n-ruby-3 px-2.5 py-1 text-xs font-medium text-n-ruby-11"
          >
            Em atraso
          </span>
        </div>

        <!-- PIX QR code -->
        <div
          v-if="pixImageSrc(primaryCharge)"
          class="flex flex-col items-center gap-2 rounded-xl border border-n-weak bg-white p-4"
        >
          <img
            :src="pixImageSrc(primaryCharge)"
            alt="QR Code PIX"
            class="h-44 w-44"
          />
          <span class="text-xs text-n-slate-10">
            Aponte a câmera do seu banco para pagar via PIX
          </span>
        </div>

        <!-- Copy PIX code -->
        <button
          v-if="primaryCharge.pix_payload"
          type="button"
          class="flex w-full items-center justify-center gap-2 rounded-xl border border-n-weak bg-n-solid-1 px-4 py-3 text-sm font-medium text-n-slate-12 transition-colors hover:bg-n-solid-2"
          @click="copyPix(primaryCharge)"
        >
          <span
            :class="
              copiedId === primaryCharge.id ? 'i-lucide-check' : 'i-lucide-copy'
            "
          />
          {{
            copiedId === primaryCharge.id
              ? 'Código PIX copiado!'
              : 'Copiar código PIX (copia e cola)'
          }}
        </button>

        <!-- Hosted invoice / boleto -->
        <a
          v-if="payLink(primaryCharge)"
          :href="payLink(primaryCharge)"
          target="_blank"
          rel="noopener noreferrer"
          class="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-amber-500 to-red-500 px-4 py-3 text-sm font-semibold text-white shadow-md transition-opacity hover:opacity-90"
        >
          <span class="i-lucide-external-link" />
          Abrir página de pagamento
        </a>
        <a
          v-if="primaryCharge.bank_slip_url && primaryCharge.invoice_url"
          :href="primaryCharge.bank_slip_url"
          target="_blank"
          rel="noopener noreferrer"
          class="flex w-full items-center justify-center gap-2 text-xs font-medium text-n-slate-11 hover:text-n-slate-12"
        >
          <span class="i-lucide-file-text" />
          Baixar boleto (PDF)
        </a>
      </div>

      <!-- Manual block / no overdue charge: clear blocked notice + contact -->
      <div v-else class="flex flex-col gap-3 px-6 pb-2">
        <div
          class="flex items-start gap-3 rounded-xl border border-n-weak bg-n-solid-1 px-4 py-3 text-left"
        >
          <span class="i-lucide-shield-alert mt-0.5 text-xl text-amber-500" />
          <p class="text-sm text-n-slate-11">
            <template v-if="isManual">
              Este acesso está bloqueado administrativamente pela equipe
              financeira. Fale com o nosso time para regularizar e liberar o
              acesso.
            </template>
            <template v-else>
              Não encontramos os detalhes da cobrança automaticamente. Fale com
              o nosso time para regularizar o pagamento.
            </template>
          </p>
        </div>
      </div>

      <!-- Extra charges -->
      <div
        v-if="showPayment && extraCharges.length"
        class="flex flex-col gap-2 px-6 pb-2"
      >
        <span
          class="text-xs font-medium uppercase tracking-wide text-n-slate-10"
        >
          Outras cobranças em aberto
        </span>
        <a
          v-for="charge in extraCharges"
          :key="charge.id"
          :href="payLink(charge) || '#'"
          :target="payLink(charge) ? '_blank' : '_self'"
          rel="noopener noreferrer"
          class="flex items-center justify-between rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 transition-colors hover:bg-n-solid-2"
        >
          <span class="flex flex-col">
            <span class="text-sm font-medium text-n-slate-12">
              {{ formatBRL(charge.amount_cents) }}
            </span>
            <span class="text-xs text-n-slate-10">
              Venc. {{ formatDate(charge.due_date) }}
            </span>
          </span>
          <span
            v-if="payLink(charge)"
            class="i-lucide-chevron-right text-n-slate-10"
          />
        </a>
      </div>

      <!-- Footer actions -->
      <div
        class="flex flex-col gap-3 border-t border-n-weak bg-n-solid-1 px-6 py-5"
      >
        <button
          type="button"
          :disabled="checking"
          class="flex w-full items-center justify-center gap-2 rounded-xl border border-n-weak bg-n-background px-4 py-2.5 text-sm font-medium text-n-slate-12 transition-colors hover:bg-n-solid-2 disabled:opacity-60"
          @click="checkPayment"
        >
          <span
            :class="
              checking
                ? 'i-lucide-loader-circle animate-spin'
                : 'i-lucide-refresh-cw'
            "
          />
          <template v-if="checking">Verificando…</template>
          <template v-else-if="isManual">Verificar status do acesso</template>
          <template v-else>Já efetuei o pagamento</template>
        </button>
        <p class="text-center text-xs text-n-slate-10">
          <template v-if="isManual">
            Assim que a equipe liberar o acesso, esta tela é atualizada
            automaticamente.
          </template>
          <template v-else>
            Assim que o pagamento for confirmado, o acesso é liberado
            automaticamente em alguns instantes.
          </template>
          <template v-if="blockedSince">
            <br />
            Bloqueado desde
            {{ new Date(blockedSince).toLocaleString('pt-BR') }}.
          </template>
        </p>
      </div>
    </div>
  </div>
</template>
